import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import AppCurrentVisits from "./AppCurrentVisits";
import { useGetMembersQuery } from "../../../features/members/membersApiSlice";
import { useGetSitesQuery } from "../../../features/sites/sitesApiSlice";
import UserRolesBadge from "../../../components/rolesbadge/UserRolesBadge";

const buildChartColors = (theme, length) => {
  const palette = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
    theme.palette.grey[500],
  ];

  if (length <= palette.length) {
    return palette.slice(0, Math.max(length, 1));
  }

  return Array.from({ length }, (_, index) => palette[index % palette.length]);
};

export default function ActiveMembersBySite() {
  const theme = useTheme();
  const { data: members = [], isLoading: isLoadingMembers } =
    useGetMembersQuery();
  const { data: sites = [], isLoading: isLoadingSites } = useGetSitesQuery();

  const chartData = useMemo(() => {
    const siteNameById = new Map(
      (sites || [])
        .map((site) => {
          const id = site?._id || site?.id || site?.value;
          const name = site?.siteName || site?.name;
          return id ? [String(id), name] : null;
        })
        .filter(Boolean),
    );

    const counts = new Map();

    (members || []).forEach((member) => {
      if ((member?.status || "").toLowerCase() !== "active") return;

      const siteRef = member?.standortRef;
      const siteId =
        typeof siteRef === "object" ? siteRef?._id || siteRef?.id : siteRef;
      const label =
        (typeof siteRef === "object" ? siteRef?.siteName : null) ||
        (siteId ? siteNameById.get(String(siteId)) : null) ||
        (siteId ? String(siteId) : "Kein Standort");

      counts.set(label, (counts.get(label) || 0) + 1);
    });

    // Ensure existing sites show up even when they currently have zero active members
    (sites || []).forEach((site) => {
      const label = site?.siteName || site?.name;
      if (label && !counts.has(label)) {
        counts.set(label, 0);
      }
    });

    const data = Array.from(counts.entries())
      .map(([label, value]) => ({ label, value }))
      .sort(
        (a, b) => b.value - a.value || a.label.localeCompare(b.label, "de-DE"),
      );

    const hasData = data.some((d) => d.value > 0);

    return hasData ? data : [{ label: "Keine aktiven Mitglieder", value: 1 }];
  }, [members, sites]);

  const chartColors = useMemo(
    () => buildChartColors(theme, chartData.length),
    [chartData.length, theme],
  );

  const isLoading = isLoadingMembers || isLoadingSites;

  return (
    <AppCurrentVisits
      title="Aktive Mitglieder je Standort"
      subheader={
        isLoading ? (
          "Lade Daten ..."
        ) : (
          <p style={{ margin: 0 }}>
            Nur für{"  "}
            <UserRolesBadge status="Admin" /> sichtbar.
          </p>
        )
      }
      chartData={chartData}
      chartColors={chartColors}
    />
  );
}
